import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useAuth0 } from "@auth0/auth0-react";
import PremPlayersContext from "../../contexts/premplayers-context";
import { LeaderboardContext } from "../../contexts/leaderboard-context";
import { SiteVarsContext } from "../../contexts/sitevars-context";
import { useSiteVarsFunctions } from "../../hooks/use-site-vars-functions";
import {
  getClubOffersResource,
  updateOfferResource,
  addOfferResource,
} from "../../services/offers.service";

export const BuySell = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const { premPlayersData, isLoadingPremPlayersData } = useContext(PremPlayersContext);
  const { leaderboardData, isLoadingLeaderboardData } = useContext(LeaderboardContext);
  const { siteVarsData, isLoadingSiteVarsData } = useContext(SiteVarsContext);
  const { getCurrentWindow, getPlayerPositionById, getTeamById, getClubName } = useSiteVarsFunctions();

  const [clubId, setClubId] = useState(null);
  const [currentWindow, setCurrentWindow] = useState(null);
  const [userPlayers, setUserPlayers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [isSellPlayerDialogueOpen, setIsSellPlayerDialogueOpen] = useState(false);
  const [selectedPlayerToSell, setSelectedPlayerToSell] = useState("");
  const [saleAmount, setSaleAmount] = useState("");
  const [selectedClubToSellTo, setSelectedClubToSellTo] = useState(0); // 0 for open market
  const [isLoadingOffers, setIsLoadingOffers] = useState(true);
  const [isLoadingUserPlayers, setIsLoadingUserPlayers] = useState(true);

  useEffect(() => {
    if (user && leaderboardData.length > 0 && premPlayersData.length > 0 && siteVarsData) {
      const userClubId = user["https://tlog-online.co.uk/club_id"];
      setClubId(userClubId);
      setCurrentWindow(getCurrentWindow());

      const userTeam = leaderboardData.find((team) => team.clubId == userClubId);

      if (userTeam && userTeam.players) {
        const players = userTeam.players.map((player) => {
          const fullPlayerData = premPlayersData.find((p) => p.playerId == player.playerId);
          return { ...player, ...fullPlayerData };
        });
        setUserPlayers(players);
      }
      setIsLoadingUserPlayers(false);
    }
  }, [user, leaderboardData, premPlayersData, siteVarsData]);

  useEffect(() => {
    const fetchOffers = async () => {
      if (clubId && currentWindow) {
        setIsLoadingOffers(true);
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getClubOffersResource(clubId, currentWindow, accessToken);

        if (data) {
          setOffers(data.offers);
        } else if (error) {
          console.error("Error fetching offers:", error);
        }
        setIsLoadingOffers(false);
      }
    };

    fetchOffers();
  }, [clubId, currentWindow, getAccessTokenSilently]);

  const handleSellPlayerClick = () => {
    setIsSellPlayerDialogueOpen(true);
  };

  const handleCloseSellPlayerDialogue = () => {
    setIsSellPlayerDialogueOpen(false);
    setSelectedPlayerToSell("");
    setSaleAmount("");
    setSelectedClubToSellTo(0); // Reset to open market
  };

  const handleSellPlayerSubmit = async () => {
    if (!selectedPlayerToSell || !saleAmount || !clubId || !currentWindow) {
      alert("Please select a player, a club to sell to, and enter a sale amount.");
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await addOfferResource(
        Number(saleAmount),
        clubId,
        Number(selectedClubToSellTo),
        Number(selectedPlayerToSell),
        "pending",
        currentWindow,
        accessToken
      );

      if (data) {
        console.log("Offer added successfully:", data);
        handleCloseSellPlayerDialogue();
        // Refresh offers
        const { data: updatedOffers, error: offersError } = await getClubOffersResource(
          clubId,
          currentWindow,
          accessToken
        );
        if (updatedOffers) {
          setOffers(updatedOffers.offers);
        } else if (offersError) {
          console.error("Error refreshing offers:", offersError);
        }
      } else if (error) {
        console.error("Error adding offer:", error);
      }
    } catch (error) {
      console.error("Error getting access token or adding offer:", error);
    }
  };

  const handleUpdateOfferStatus = async (offerId, status) => {
    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await updateOfferResource(offerId, status, accessToken);

      if (data) {
        console.log(`Offer ${status} successfully:`, data);
        // Refresh offers
        const { data: updatedOffers, error: offersError } = await getClubOffersResource(
          clubId,
          currentWindow,
          accessToken
        );
        if (updatedOffers) {
          setOffers(updatedOffers.offers);
        } else if (offersError) {
          console.error("Error refreshing offers:", offersError);
        }
      } else if (error) {
        console.error(`Error ${status} offer:`, error);
      }
    } catch (error) {
      console.error("Error getting access token or updating offer:", error);
    }
  };

  const isLoading = isLoadingPremPlayersData || isLoadingLeaderboardData || isLoadingSiteVarsData || isLoadingOffers || isLoadingUserPlayers;

  return (
    <div className="content-layout">
      <div className="content__body" style={{ textAlign: "center", marginTop: "20px" }}>
        <Box sx={{ marginBottom: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSellPlayerClick}>
            Sell Player
          </Button>
        </Box>

        <Dialog open={isSellPlayerDialogueOpen} onClose={handleCloseSellPlayerDialogue}>
          <DialogTitle>Sell Player</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
              <InputLabel id="select-player-label">Player</InputLabel>
              <Select
                labelId="select-player-label"
                value={selectedPlayerToSell}
                label="Player"
                onChange={(e) => setSelectedPlayerToSell(e.target.value)}
              >
                {userPlayers.map((player) => (
                  <MenuItem key={player.playerId} value={player.playerId}>
                    {player.lastName} ({getPlayerPositionById(player.positionId).name_short})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Sale Amount"
              type="number"
              fullWidth
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
            />
            <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
              <InputLabel id="select-club-label">Sell To Club</InputLabel>
              <Select
                labelId="select-club-label"
                value={selectedClubToSellTo}
                label="Sell To Club"
                onChange={(e) => setSelectedClubToSellTo(e.target.value)}
              >
                <MenuItem value={0}>Open Market</MenuItem>
                {leaderboardData
                  .filter((team) => team.clubId !== clubId) // Exclude current club
                  .map((team) => (
                    <MenuItem key={team.clubId} value={team.clubId}>
                      {team.clubName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSellPlayerDialogue}>Cancel</Button>
            <Button onClick={handleSellPlayerSubmit} color="primary">
              Submit Sale
            </Button>
          </DialogActions>
        </Dialog>

        <TableContainer component={Paper} sx={{ overflowY: "auto", marginBottom: 5 }}>
          {isLoading ? (
            <Stack spacing={1}>
              <Skeleton variant="rounded" width={"100%"} height={30} />
              <Skeleton variant="rounded" width={"100%"} height={26} />
              <Skeleton variant="rounded" width={"100%"} height={26} />
              <Skeleton variant="rounded" width={"100%"} height={26} />
            </Stack>
          ) : (
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Clubs</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No offers found.
                    </TableCell>
                  </TableRow>
                ) : (
                  offers.map((offer) => {
                    const player = premPlayersData.find((p) => p.playerId === offer.playerId);
                    const fromClubName = getClubName(offer.from);
                    const toClubName = offer.to === 0 ? "Open Market" : getClubName(offer.to);

                    return (
                      <TableRow key={offer.offerId}>
                        <TableCell>
                          {player ? `${player.lastName} (${getPlayerPositionById(player.positionId).name_short})` : "Unknown Player"}
                        </TableCell>
                        <TableCell>${offer.amount}</TableCell>
                        <TableCell>From: {fromClubName}<br />To: {toClubName}</TableCell>
                        <TableCell>{offer.status}</TableCell>
                        <TableCell>
                          {offer.from === clubId && offer.status === "pending" && (
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              onClick={() => handleUpdateOfferStatus(offer.offerId, "cancelled")}
                            >
                              Cancel
                            </Button>
                          )}
                          {(offer.to === clubId || offer.to === 0) && offer.status === "pending" && (
                            <>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => handleUpdateOfferStatus(offer.offerId, "accepted")}
                              >
                                Accept
                              </Button>
                              {offer.to === clubId && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleUpdateOfferStatus(offer.offerId, "rejected")}
                                >
                                  Reject
                                </Button>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </div>
    </div>
  );
};